import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Target, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { formatRp, parseNumber, formatThousands } from '../lib/format';
import { MP_KEYS, mpCalc, mpRecommend } from '../lib/marketplaceCalc';

const LABELS = { shopee: 'Shopee', tiktok: 'TikTok Shop', minimo: 'Minimo' };
const fmt = (n) => (n ? formatThousands(String(Math.round(n))) : '');
const num = (s) => Number(parseNumber(s) || 0);

const MoneyInput = ({ label, value, onChange, testid, hint }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{label}</Label>
    <Input value={value} onChange={(e) => onChange(formatThousands(e.target.value))}
      inputMode="numeric" placeholder={hint || '0'}
      className="h-9 text-right font-mono text-sm" data-testid={testid} />
  </div>
);

const Row = ({ label, value, minus, strong, testid, tone }) => (
  <div className={`flex justify-between py-1.5 text-sm ${strong ? 'border-t border-stone-200 font-semibold' : ''}`}>
    <span className="text-stone-600">{label}</span>
    <span className={`font-mono ${tone || (minus && value > 0 ? 'text-red-700' : minus ? 'text-stone-400' : 'text-stone-900')}`} data-testid={testid}>
      {minus && value > 0 ? '−' : ''}{formatRp(value)}
    </span>
  </div>
);

export const Breakdown = ({ r, fee, prefix }) => {
  const pos = r.profit >= 0;
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50/60 px-4 py-2" data-testid={`${prefix}-breakdown`}>
      <Row label="Harga jual bruto" value={r.bruto} testid={`${prefix}-bruto`} />
      <Row label="HPP" value={r.hpp} minus testid={`${prefix}-hpp`} />
      <Row label={`Biaya admin (${fee.admin_pct}%${fee.admin_max ? `, maks ${formatRp(fee.admin_max)}` : ''})`} value={r.admin} minus testid={`${prefix}-admin`} />
      <Row label={`Gratis ongkir (${fee.ongkir_pct}%${fee.ongkir_max ? `, maks ${formatRp(fee.ongkir_max)}` : ''})`} value={r.ongkir} minus testid={`${prefix}-ongkir`} />
      <Row label="Biaya proses pesanan" value={r.proses} minus testid={`${prefix}-proses`} />
      <Row label={`PPh Final ${fee.pph_pct}% (dari omzet bruto)`} value={r.pph} minus testid={`${prefix}-pph`} />
      <Row label="Biaya packing" value={r.packing} minus testid={`${prefix}-packing`} />
      <Row label="Total seluruh biaya" value={r.total_biaya} strong testid={`${prefix}-total-biaya`} tone="text-red-800" />
      <Row label="Dana bersih diterima" value={r.neto} strong testid={`${prefix}-neto`} tone="text-sky-900" />
      <div className={`flex justify-between items-baseline py-2 mt-1 border-t border-stone-300 ${pos ? 'text-emerald-900' : 'text-red-800'}`}>
        <span className="font-semibold text-sm">Laba bersih</span>
        <span className="text-right">
          <span className="font-mono font-bold text-lg" data-testid={`${prefix}-profit`}>{formatRp(r.profit)}</span>
          <span className="block text-xs font-medium" data-testid={`${prefix}-margin`}>{r.margin.toFixed(2)}% margin</span>
        </span>
      </div>
    </div>
  );
};

const MpSelect = ({ value, onChange, testid }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Marketplace</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {MP_KEYS.map((k) => <SelectItem key={k} value={k} data-testid={`${testid}-${k}`}>{LABELS[k]}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const SimulationPanel = ({ fees, init }) => {
  const [mp, setMp] = useState(init.marketplace);
  const [harga, setHarga] = useState(fmt(init.harga));
  const [hpp, setHpp] = useState(fmt(init.hpp));
  const [orders, setOrders] = useState('1');
  const [packing, setPacking] = useState('');
  const [adv, setAdv] = useState(false);
  const [bases, setBases] = useState({ admin: '', ongkir: '', pajak: '' });
  const r = mpCalc(num(harga), num(hpp), fees[mp], {
    orders, packing: num(packing),
    adminBase: adv && bases.admin ? num(bases.admin) : undefined,
    ongkirBase: adv && bases.ongkir ? num(bases.ongkir) : undefined,
    pajakBase: adv && bases.pajak ? num(bases.pajak) : undefined,
  });
  const n = r.orders;
  return (
    <div className="space-y-4" data-testid="sim-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MpSelect value={mp} onChange={setMp} testid="sim-mp" />
        <MoneyInput label="Harga jual produk" value={harga} onChange={setHarga} testid="sim-harga" />
        <MoneyInput label="HPP produk" value={hpp} onChange={setHpp} testid="sim-hpp" />
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Jumlah pesanan</Label>
          <Input type="number" min="1" value={orders} onChange={(e) => setOrders(e.target.value)}
            className="h-9 text-right font-mono text-sm" data-testid="sim-orders" />
        </div>
        <MoneyInput label="Biaya packing" value={packing} onChange={setPacking} testid="sim-packing" />
      </div>
      <button type="button" onClick={() => setAdv((v) => !v)}
        className="text-xs text-emerald-800 underline underline-offset-2" data-testid="sim-toggle-adv">
        {adv ? 'Sembunyikan' : 'Sesuaikan'} dasar pengenaan (sesuai laporan marketplace)
      </button>
      {adv && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-lg border border-dashed border-stone-300 p-3">
          <MoneyInput label="Dasar admin" hint="= harga bruto" value={bases.admin}
            onChange={(v) => setBases((b) => ({ ...b, admin: v }))} testid="sim-base-admin" />
          <MoneyInput label="Dasar gratis ongkir" hint="= harga bruto" value={bases.ongkir}
            onChange={(v) => setBases((b) => ({ ...b, ongkir: v }))} testid="sim-base-ongkir" />
          <MoneyInput label="Dasar pajak (DPP)" hint="= harga bruto" value={bases.pajak}
            onChange={(v) => setBases((b) => ({ ...b, pajak: v }))} testid="sim-base-pajak" />
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-2">Per pesanan</div>
          <Breakdown r={r} fee={fees[mp]} prefix="sim" />
        </div>
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-2">Total {n} pesanan</div>
          <Breakdown r={{ ...r.total, margin: r.margin }} fee={fees[mp]} prefix="sim-total" />
        </div>
      </div>
    </div>
  );
};

const MODES = [
  { value: 'nominal', label: 'Laba nominal (Rp)' },
  { value: 'margin', label: 'Margin (% dari harga jual)' },
  { value: 'markup', label: 'Markup (% dari HPP)' },
];

const RecommendPanel = ({ fees, init, onApply }) => {
  const [mp, setMp] = useState(init.marketplace);
  const [hpp, setHpp] = useState(fmt(init.hpp));
  const [packing, setPacking] = useState('');
  const [mode, setMode] = useState('nominal');
  const [target, setTarget] = useState('');
  const t = mode === 'nominal' ? num(target) : Number(String(target).replace(',', '.')) || 0;
  const res = useMemo(() => mpRecommend(num(hpp), fees[mp], mode, t, num(packing)),
    [hpp, fees, mp, mode, t, packing]);
  const rounded = res ? Math.ceil(res.price / 1000) * 1000 : 0;
  return (
    <div className="space-y-4" data-testid="rec-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MpSelect value={mp} onChange={setMp} testid="rec-mp" />
        <MoneyInput label="HPP" value={hpp} onChange={setHpp} testid="rec-hpp" />
        <MoneyInput label="Biaya packing" value={packing} onChange={setPacking} testid="rec-packing" />
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Jenis target</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-9" data-testid="rec-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODES.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`rec-mode-${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">
            Target {mode === 'nominal' ? '(Rp)' : '(%)'}
          </Label>
          <Input value={target} inputMode="decimal" placeholder="0"
            onChange={(e) => setTarget(mode === 'nominal' ? formatThousands(e.target.value) : e.target.value.replace(/[^0-9.,]/g, ''))}
            className="h-9 text-right font-mono text-sm" data-testid="rec-target" />
        </div>
      </div>
      {!res ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="rec-impossible">
          Target tidak mungkin dicapai dengan tarif {LABELS[mp]} saat ini.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl bg-emerald-900 text-white p-5 flex flex-col justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-200">Harga jual minimum</div>
              <div className="text-3xl font-bold font-mono mt-1" data-testid="rec-price">{formatRp(res.price)}</div>
              <div className="text-xs text-emerald-200 mt-1">
                Laba tercapai {formatRp(res.breakdown.profit)} (target {formatRp(res.target_profit)})
              </div>
              <div className="text-xs text-emerald-100 mt-3">
                Dibulatkan ke atas: <b className="font-mono" data-testid="rec-price-rounded">{formatRp(rounded)}</b>
              </div>
            </div>
            {onApply && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => onApply(mp, res.price)} data-testid="rec-apply-exact"
                  className="bg-white text-emerald-900 hover:bg-emerald-50 gap-1">
                  <Check className="w-3.5 h-3.5" /> Pakai {formatRp(res.price)}
                </Button>
                <Button size="sm" variant="outline" onClick={() => onApply(mp, rounded)} data-testid="rec-apply-rounded"
                  className="bg-transparent border-emerald-300 text-white hover:bg-emerald-800 gap-1">
                  Pakai {formatRp(rounded)}
                </Button>
              </div>
            )}
          </div>
          <Breakdown r={res.breakdown} fee={fees[mp]} prefix="rec" />
        </div>
      )}
    </div>
  );
};

export const MarketplaceSimulator = ({ open, onOpenChange, fees, init, onApply }) => {
  const [tab, setTab] = useState('sim');
  const [key, setKey] = useState(0);
  useEffect(() => { if (open) { setKey((k) => k + 1); setTab(init.tab || 'sim'); } }, [open, init]);
  const tabCls = (t) => `px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === t ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-800'}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="simulator-dialog">
        <DialogHeader>
          <DialogTitle>Simulasi Perhitungan Marketplace</DialogTitle>
          <DialogDescription>
            {init.label ? <>Paket: <b>{init.label}</b>. </> : null}
            1 pesanan = 1 produk. PPh Final dihitung dari omzet bruto, terpisah dari biaya admin.
          </DialogDescription>
        </DialogHeader>
        <div className="inline-flex bg-stone-100 rounded-lg p-1 gap-1 w-fit">
          <button type="button" className={tabCls('sim')} onClick={() => setTab('sim')} data-testid="tab-sim">
            <Calculator className="w-3.5 h-3.5" /> Simulasi
          </button>
          <button type="button" className={tabCls('rec')} onClick={() => setTab('rec')} data-testid="tab-rec">
            <Target className="w-3.5 h-3.5" /> Harga Rekomendasi
          </button>
        </div>
        {tab === 'sim'
          ? <SimulationPanel key={`s${key}`} fees={fees} init={init} />
          : <RecommendPanel key={`r${key}`} fees={fees} init={init} onApply={onApply} />}
      </DialogContent>
    </Dialog>
  );
};
